"use client";

import { faChrome } from "@awesome.me/kit-18b0dd684a/icons/classic/brands";
import {
  faBalanceScale,
  faBars,
  faBrowser,
  faBuilding,
  faCalendarDay,
  faCartShopping,
  faChartMixedUpCircleDollar,
  faChartUser,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCode,
  faCubes,
  faFileCertificate,
  faForkKnife,
  faGraduationCap,
  faHashtag,
  faHouse,
  faInputPipe,
  faMagicWandSparkles,
  faMagnifyingGlassChart,
  faMonitorWaveform,
  faNewspaper,
  faRocketLaunch,
  faShield,
  faSuitcase,
  faTag,
  faTicket,
  faUmbrella,
  faUser,
  faUsers,
  faXmark,
} from "@awesome.me/kit-18b0dd684a/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { motion } from "motion/react";
import { StaticImageData } from "next/image";
import Link from "next/link";
import React, { useState } from "react";
import navData from "~/public/home/navData.json";
import Logo from "../../components/Front/Logo";

export type SubmenuType = {
  title?: string;
  content: {
    title: string;
    description?: string;
    link: string;
    target?: string;
    icon?: React.ReactNode;
  }[];
  toggleNav?: () => void;
};

export type MenuBlockType = {
  title: string;
  link?: string;
  splitColumn?: boolean;
  order?: number;
  columns: ColumnType[];
};

export type ColumnType = {
  className?: string;
  title?: string;
  description?: string;
  backgroundImage?: string | StaticImageData[];
  bgImgClassname?: string;
  buttons?: { title: string; link: string }[];
  submenu?: SubmenuType[];
};

export type NavDataType = MenuBlockType[];

type HomepageHeaderProps = {
  showUrlOnScroll?: boolean;
};

const HomepageHeader = ({ showUrlOnScroll }: HomepageHeaderProps) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const headerNavData = navData.map((menuBlock) => ({
    ...menuBlock,
    columns: menuBlock.columns.map((column) => ({
      ...column,
      submenu: column.submenu?.map((submenu) => ({
        ...submenu,
        content: submenu.content.map((item) => ({
          ...item,
          icon: iconMap[item.icon as string],
        })),
      })),
    })),
  }));

  return (
    <header className="relative z-20 bg-vpDarkBlue-500 pt-16">
      <div className="fixed top-0 flex h-16 w-full items-center bg-vpDarkBlue-500/70 px-4 md:px-6 lg:px-8">
        <Link href="https://visualping.io/" className="mr-10" aria-label="Visualping">
          <Logo white />
        </Link>
        <div className="hidden w-full items-center lg:flex">
          <Nav data={headerNavData} />
          <HeaderActions />
        </div>
        <div className="ml-auto lg:hidden">
          <button
            onClick={() => setShowMobileMenu((s) => !s)}
            className="flex size-10 items-center justify-center rounded-lg text-white hover:bg-white/10"
            aria-label="Open menu"
          >
            <FontAwesomeIcon icon={faBars} className="size-5" />
          </button>
        </div>
        {showMobileMenu && (
          <div className="lg:hidden">
            <MobileNav toggleNav={() => setShowMobileMenu((s) => !s)} data={headerNavData} />
          </div>
        )}
      </div>
    </header>
  );
};

const Nav = ({ data }: { data: NavDataType }) => {
  return (
    <nav>
      <ul className="flex items-center space-x-2 text-white">
        {data.map((item) => (
          <NavItem key={item.title} item={item} />
        ))}
      </ul>
    </nav>
  );
};

const NavItem = ({ item }: { item: NavDataType[number] }) => {
  return (
    <>
      <li className="group relative">
        <Link
          href={item?.link ?? "#"}
          className="flex items-center gap-2 rounded-md px-4 py-1.5 font-semibold transition hover:bg-white/5 group-hover:bg-white/5"
        >
          {item.title}
          {item?.columns?.length ? (
            <FontAwesomeIcon icon={faChevronDown} className="size-3 group-hover:rotate-180" />
          ) : null}
        </Link>
        {item?.columns && (
          <div
            className={classNames("absolute top-full hidden pt-3 group-hover:block", {
              "max-xl:-left-64": item?.title === "Customers",
            })}
          >
            <div className="flex h-full overflow-hidden rounded-md bg-white text-black shadow-lg">
              {item.columns.map((column, columnIndex) => (
                <div
                  key={columnIndex}
                  className={classNames("flex flex-col bg-right-bottom bg-no-repeat", column?.className, {
                    "[&_.nav-item-icon]:bg-white [&_.nav-item:hover]:bg-vpGray-50":
                      column?.className === "bg-vpGray-200",
                  })}
                >
                  {column.submenu?.map((submenu, submenuIndex) => (
                    <Submenu key={submenuIndex} title={submenu?.title} content={submenu.content} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </li>
    </>
  );
};

const Submenu = ({ title, content, toggleNav }: SubmenuType) => {
  return (
    <div className="py-4">
      {title && <h3 className="mb-3 px-6 text-sm font-bold uppercase">{title}</h3>}
      <div>
        {content.map((item) => (
          <Link
            key={item.title}
            href={item?.link}
            onClick={() => {
              toggleNav?.();
            }}
            className="nav-item flex items-start gap-4 px-6 py-2 transition-colors hover:bg-vpGray-200 md:items-center"
            aria-label={item.title}
          >
            <div className="nav-item-icon flex size-10 items-center justify-center rounded-md bg-vpBlue-100 text-vpBlue-500 [&_svg]:size-5">
              {item.icon}
            </div>
            <div className="flex-1 text-sm">
              <div className="font-semibold md:whitespace-nowrap">{item.title}</div>
              <div className="text-vpGray-600 md:whitespace-nowrap">{item.description}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const HeaderActions = () => {
  return (
    <div className="ml-auto flex items-center space-x-6 text-white">
      <Link
        href="https://visualping.io/contact"
        onClick={() => {}}
        className="rounded-md px-4 py-1.5 font-semibold transition hover:bg-white/5"
      >
        Book a demo
      </Link>
      <Link
        href="https://visualping.io/login"
        onClick={() => {}}
        className="rounded-md px-4 py-1.5 font-semibold transition hover:bg-white/5"
      >
        Login
      </Link>

      <Link
        href="https://visualping.io/sign-up"
        onClick={() => {}}
        className="border-1 rounded-lg border-white px-4 py-1.5 transition hover:border-vpBlue-500 hover:bg-vpBlue-500 hover:text-white"
      >
        <span className="font-semibold">Get started</span> it&apos;s free
      </Link>
    </div>
  );
};

type MobileNavProps = {
  toggleNav: () => void;
  data: NavDataType;
};

const MobileNav = ({ toggleNav, data }: MobileNavProps) => {
  const [menuOpen, setMenuOpen] = useState("");

  const menuOpenData = data.find((item) => item.title === menuOpen);
  const submenus = menuOpenData?.columns.flatMap((column) => column.submenu || []);

  return (
    <div className="fixed inset-0 z-20 flex h-dvh flex-col bg-white">
      <header>
        <div className="flex h-16 items-center justify-between border-b px-4 md:px-6">
          {menuOpen ? (
            <button
              className="-ml-2 flex items-center gap-2 rounded-lg px-4 py-2 text-vpBlue-500 hover:bg-vpBlue-100"
              onClick={() => setMenuOpen("")}
            >
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </button>
          ) : (
            <Link href="https://visualping.io/" className="mr-10" aria-label="Visualping">
              <Logo />
            </Link>
          )}
          <div>
            <button
              onClick={toggleNav}
              className="mt-1 flex size-10 items-center justify-center rounded-lg hover:bg-vpBlue-100"
              aria-label="Close menu"
            >
              <FontAwesomeIcon icon={faXmark} className="size-5" />
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto overscroll-contain pt-4">
        {menuOpen ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {submenus?.map((submenu, index) => (
              <Submenu
                key={index}
                title={submenu?.title ?? menuOpenData?.title}
                content={submenu.content}
                toggleNav={toggleNav}
              />
            ))}
          </motion.div>
        ) : (
          <nav>
            <ul className="flex flex-col">
              {data.map((item) => (
                <button
                  key={item.title}
                  className="flex items-center justify-between px-6 py-4 text-xl font-semibold hover:bg-vpGray-100"
                  onClick={() => setMenuOpen(item.title)}
                >
                  {item.title} <FontAwesomeIcon icon={faChevronRight} />
                </button>
              ))}
              <Link
                href="https://visualping.io/contact"
                onClick={() => {}}
                className="flex items-center justify-between px-6 py-4 text-xl font-semibold hover:bg-vpGray-100"
              >
                Book a demo
              </Link>
              <Link
                href="https://visualping.io/login"
                onClick={() => {}}
                className="flex items-center justify-between px-6 py-4 text-xl font-semibold hover:bg-vpGray-100"
              >
                Login
              </Link>
            </ul>
          </nav>
        )}
      </main>
      <footer className="mt-auto bg-white p-4 md:p-6">
        <Link
          href="https://visualping.io/sign-up"
          onClick={() => {}}
          className="block w-full rounded-lg border-white bg-vpBlue-500 p-4 text-center text-lg text-white transition hover:bg-vpBlue-700"
        >
          <span className="font-semibold">Get started</span> it&apos;s free
        </Link>
      </footer>
    </div>
  );
};

export default HomepageHeader;

const iconMap: Record<string, JSX.Element> = {
  faBalanceScale: <FontAwesomeIcon icon={faBalanceScale} />,
  faBars: <FontAwesomeIcon icon={faBars} />,
  faBrowser: <FontAwesomeIcon icon={faBrowser} />,
  faBuilding: <FontAwesomeIcon icon={faBuilding} />,
  faCalendarDay: <FontAwesomeIcon icon={faCalendarDay} />,
  faCartShopping: <FontAwesomeIcon icon={faCartShopping} />,
  faChartMixedUpCircleDollar: <FontAwesomeIcon icon={faChartMixedUpCircleDollar} />,
  faChartUser: <FontAwesomeIcon icon={faChartUser} />,
  faChevronDown: <FontAwesomeIcon icon={faChevronDown} />,
  faChevronLeft: <FontAwesomeIcon icon={faChevronLeft} />,
  faChevronRight: <FontAwesomeIcon icon={faChevronRight} />,
  faCode: <FontAwesomeIcon icon={faCode} />,
  faCubes: <FontAwesomeIcon icon={faCubes} />,
  faFileCertificate: <FontAwesomeIcon icon={faFileCertificate} />,
  faForkKnife: <FontAwesomeIcon icon={faForkKnife} />,
  faGraduationCap: <FontAwesomeIcon icon={faGraduationCap} />,
  faHashtag: <FontAwesomeIcon icon={faHashtag} />,
  faHouse: <FontAwesomeIcon icon={faHouse} />,
  faInputPipe: <FontAwesomeIcon icon={faInputPipe} />,
  faMagicWandSparkles: <FontAwesomeIcon icon={faMagicWandSparkles} />,
  faMagnifyingGlassChart: <FontAwesomeIcon icon={faMagnifyingGlassChart} />,
  faMonitorWaveform: <FontAwesomeIcon icon={faMonitorWaveform} />,
  faNewspaper: <FontAwesomeIcon icon={faNewspaper} />,
  faRocketLaunch: <FontAwesomeIcon icon={faRocketLaunch} />,
  faShield: <FontAwesomeIcon icon={faShield} />,
  faSuitcase: <FontAwesomeIcon icon={faSuitcase} />,
  faTag: <FontAwesomeIcon icon={faTag} />,
  faTicket: <FontAwesomeIcon icon={faTicket} />,
  faUmbrella: <FontAwesomeIcon icon={faUmbrella} />,
  faUser: <FontAwesomeIcon icon={faUser} />,
  faUsers: <FontAwesomeIcon icon={faUsers} />,
  faXmark: <FontAwesomeIcon icon={faXmark} />,
  faChrome: <FontAwesomeIcon icon={faChrome} />,
  aiSvgIcon: (
    <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M21.9106 12.4997C15.1546 14.1233 13.9652 15.4382 12.5002 22.9163C11.0336 15.4365 9.84594 14.1216 3.08984 12.4997C9.8442 10.8778 11.0336 9.56285 12.4985 2.08301C13.9652 9.56285 15.1528 10.8778 21.9089 12.4997H21.9106Z"
        fill="currentColor"
      />
      <path
        d="M9.375 5.72884C7.13144 6.29711 6.73647 6.75734 6.25 9.37468C5.76295 6.75673 5.36856 6.2965 3.125 5.72884C5.36798 5.16118 5.76295 4.70095 6.24942 2.08301C6.73647 4.70095 7.13086 5.16118 9.37442 5.72884H9.375Z"
        fill="currentColor"
      />
    </svg>
  ),
};
